import { useState, useCallback } from 'react';
import { Platform, Alert } from 'react-native';
import { useGetSecureAttachmentQuery } from '../store/api/QueueApi';

/**
 * Custom hook for opening secure files using RTK Query
 * @param {string} queueUserId - The queue user ID
 * @param {string} objectName - The file object name
 * @returns {Object} - Object containing openFile function and loading state
 */
export const useSecureFileOpener = (queueUserId, objectName) => {
  const [isOpening, setIsOpening] = useState(false);

  // RTK Query hook for getting secure attachment
  const {
    data: fileBlob,
    error,
    isLoading,
    refetch,
  } = useGetSecureAttachmentQuery(
    { queueUserId, objectName },
    {
      skip: !queueUserId || !objectName,
      // Don't cache the blob data
      keepUnusedDataFor: 0,
    }
  );

  const openFile = useCallback(async () => {
    if (!queueUserId || !objectName) {
      console.warn('useSecureFileOpener: Missing queueUserId or objectName');
      return;
    }

    if (isOpening) {
      return;
    }

    try {
      setIsOpening(true);

      // Trigger the query
      const result = await refetch();

      if (result.data) {
        // Handle the new response format that contains the binary data as an array
        const binaryData = result.data.data || result.data;

        if (Array.isArray(binaryData)) {
          // Convert array of numbers back to Uint8Array
          const uint8Array = new Uint8Array(binaryData);

          if (Platform.OS === 'web') {
            // For web, create blob URL and open in new tab
            const blob = new Blob([uint8Array], {
              type: result.data.type || 'application/octet-stream',
            });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');

            // Clean up blob URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 1000);
          } else {
            // For mobile, you might want to use a different approach
            // like downloading to temp directory and opening with system viewer
            Alert.alert(
              'File Ready',
              'File has been downloaded. Please check your downloads folder or use a file manager to open it.'
            );
          }
        } else if (binaryData instanceof Blob) {
          // Fallback for old format (if any)
          if (Platform.OS === 'web') {
            const blobUrl = URL.createObjectURL(binaryData);
            window.open(blobUrl, '_blank');

            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 1000);
          } else {
            Alert.alert(
              'File Ready',
              'File has been downloaded. Please check your downloads folder or use a file manager to open it.'
            );
          }
        } else {
          throw new Error('Invalid response format: binary data not found');
        }
      } else if (result.error) {
        throw result.error;
      }
    } catch (error) {
      console.error('Error opening secure file:', error);
      Alert.alert(
        'Error Opening File',
        'Failed to open the file securely. Please try again or contact support if the issue persists.'
      );
    } finally {
      setIsOpening(false);
    }
  }, [queueUserId, objectName, isOpening, refetch]);

  return {
    openFile,
    isLoading: isLoading || isOpening,
    error,
    hasData: !!fileBlob,
  };
};
